import React from 'react';

import { css, cx } from '@emotion/css';
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { UseFormReturn, useForm } from 'react-hook-form';

import { useStyles } from '@/hooks/useStyles.ts';
import { Queries } from '@/queries/Queries.ts';
import { Form } from '@/uiKit/atoms/Form.tsx';
import {
  CarePlanCreationInfo,
  CarePlanCreationInfoWithPrescription,
  GestationalNoInsulinCarePlanCreationInfo,
  SimpleCarePlanCreationInfo,
  carePlanCreationSchema,
} from '@/uiKit/organisms/PatientCreationAndRenewal/CarePlan/CarePlanCreation.ts';

export const CARE_PLAN_CREATION_FORM_ID = 'care-plan-creation-info-form';

export type CarePlanCreationFormProps = {
  carePlanInfo?: Partial<CarePlanCreationInfo>;
  onPrescription: (data: CarePlanCreationInfoWithPrescription) => void;
  onCreateNoInsulin: (data: GestationalNoInsulinCarePlanCreationInfo) => void;
  className?: string;
  children?:
    | React.ReactNode
    | ((
        formContext: UseFormReturn<SimpleCarePlanCreationInfo>,
      ) => React.ReactNode);
};

export const CarePlanCreationForm: React.FC<CarePlanCreationFormProps> = ({
  carePlanInfo,
  onPrescription,
  onCreateNoInsulin,
  children,
  className,
}) => {
  const styles = useStyles(makeStyles);
  const providers = Queries.practitioner.useHSPList({});
  const defaultProvider =
    providers.length === 0
      ? 'other'
      : providers.length === 1
        ? providers[0].hspId
        : undefined;
  const form = useForm<SimpleCarePlanCreationInfo>({
    resolver: standardSchemaResolver(carePlanCreationSchema),
    defaultValues: {
      duration: '3',
      homeServiceProviderId: defaultProvider,
      ...carePlanInfo,
    },
  });

  const onSubmit = (data: SimpleCarePlanCreationInfo) => {
    if (
      data.diabetesType === 'gestational' &&
      data.insulinScheme === 'noInsulin'
    ) {
      onCreateNoInsulin(data as GestationalNoInsulinCarePlanCreationInfo);
    } else {
      onPrescription(data as CarePlanCreationInfoWithPrescription);
    }
  };

  return (
    <Form
      id={CARE_PLAN_CREATION_FORM_ID}
      formContext={form}
      onSubmit={onSubmit}
      className={cx(styles.container, className)}
    >
      {typeof children === 'function' ? children(form) : children}
    </Form>
  );
};

const makeStyles = () => ({
  container: css({}),
});
